import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import PermissionRequiredBanner from '@/components/PermissionRequiredBanner';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/session';
import { hasPermission } from '@/lib/permissions';
import type { Database } from '@/lib/database.types';

// ============================================================
// ReorderPage — Phase 2, Epic 2C.3 + 2C.4 + 2C.5 + 2C.6 + 2D.1
// ============================================================
// 2C.3 — Pending list, vendor-grouped, +/- qty stepper for
// managers, per-vendor approve, approve-all.
//
// 2C.4 — Filter tabs (Pending / Sent) and the approval trail
// on sent rows. Sent rows render as read-only with a
// "Approved by X · 2h ago" line under the product. The
// approver's display name resolves from `public.users`
// (full_name → email_cached → "Someone" fallback). Approve
// flips items to status='sent' in place rather than removing
// them from local state, so they appear in the Sent tab
// immediately without a refetch.
//
// 2C.5 — Per-row "Remove from queue" (manager+, pending tab
// only). Soft-delete via status='cancelled' rather than DELETE
// so the audit trail stays intact. The partial unique index
// on (clinic_id, product_id) WHERE status='pending' frees up
// when status flips to 'cancelled' — re-enqueueing the same
// product (manually via 2C.7 or automatically via 2C.2) just
// works without colliding. No confirm dialog: removal is
// reversible by re-enqueue, and the partner's "Are you sure"
// feedback was specifically about Reorder Now (intent path),
// not removal.
//
// 2C.6 — Burn-rate-aware suggested qty hint. A 4th parallel
// fetch pulls `inventory_events` over the 28-day window
// (same shape as InventoryPage), aggregates per-product
// consumption, and attaches a `suggestedQty` to each queue
// item using the demo's formula:
//   suggested = max(1, ceil(monthlyBurn * 1.2))   // ~5 weeks
// When item.qty matches, a small "4-week burn rate" tag
// shows under the stepper. When it differs, a tappable
// "suggest N" pill replaces the tag — one tap sets qty to
// the suggestion via the existing updateQty path. When a
// product has no usage history in the window, monthlyBurn
// is null and the hint hides entirely.
//
// 2D.1 — "Send approval emails" CTA on the pending tab
// (manager+ only). Calls the `generate-approval-email` Edge
// Function, which composes one email per vendor with a JWT-
// signed approve link.
//
// 2D.5 — In-app per-vendor "Approve" and "Approve all" buttons
// route through the same `handle-approval-link` Edge Function
// as the email-link path (the function dispatches on body
// shape). Approve-all fans out via Promise.all — one invocation
// per vendor group. The end state is identical to the email
// flow: `approve_reorder_batch` RPC creates the orders +
// order_items rows, flips queue rows to 'sent', and the vendor
// email fires server-side via `triggerVendorOrderEmail`.
// Non-optimistic UI: the button shows pending state, awaits the
// function response, and stamps local items as 'sent' only on
// success. On `already_approved` the displayed approver may
// briefly be the caller until the next refetch resolves the
// real one — accepted edge case (race between in-app + email
// click on the same vendor group).
//
// Out of scope (deferred):
//   - 2C.7 (proposed, from FEEDBACK.md): manual reorder paths.
//
// Approve semantics carry-forward (2D):
//   queue ↔ orders relationship resolved 2026-04-29 in favor
//   of option (a) — orders rows instantiated on approve with
//   source='auto_low_par'. The 20260429160000 migration adds
//   the nullable `reorder_queue.order_id` column. 2D.2 writes
//   it; 2D.1 just signs the JWT carrying the queue row IDs.
//
// Refresh strategy:
//   Refetch on mount and after a failed update. After a
//   successful approve, items flip to 'sent' locally; we
//   don't refetch. Auto-polling for newly-enqueued items
//   isn't worth the wire noise for an MVP; pull-to-refresh /
//   route revisit covers the case.
// ============================================================

// ---- Types ----------------------------------------------------------

type ProductRow = Database['public']['Tables']['products']['Row'];
type VendorRow = Database['public']['Tables']['vendors']['Row'];
type UserRow = Database['public']['Tables']['users']['Row'];

type QueueStatus = 'pending' | 'sent' | 'received' | 'cancelled';

function isQueueStatus(value: string): value is QueueStatus {
  return (
    value === 'pending' ||
    value === 'sent' ||
    value === 'received' ||
    value === 'cancelled'
  );
}

type FilterTab = 'pending' | 'sent';

interface ApprovalInfo {
  /** Display name resolved from full_name → email_cached → 'Someone'. */
  byName: string;
  /** ISO timestamp from approved_at. */
  at: string;
}

interface QueueItem {
  id: string;
  qty: number;
  status: QueueStatus;
  product: Pick<ProductRow, 'id' | 'name' | 'uom' | 'unit_price'>;
  vendor: Pick<VendorRow, 'id' | 'name'>;
  /** Populated only for status='sent' rows; null otherwise. */
  approval: ApprovalInfo | null;
  /** The order this queue row was instantiated into on approval.
   *  Populated for sent rows; the receive screen target. */
  orderId: string | null;
  /**
   * Burn-rate-derived suggested order qty (2C.6). Null when the
   * product has no usage events in the burn-rate window — we don't
   * surface a suggestion we can't justify.
   */
  suggestedQty: number | null;
}

interface VendorGroup {
  vendor: Pick<VendorRow, 'id' | 'name'>;
  items: QueueItem[];
  subtotal: number;
}

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; items: QueueItem[] };

// ---- 2D.1 email send result (mirror of Edge Function response) ----

/**
 * Per-(vendor × recipient) outcome from `generate-approval-email`.
 * Mirrors the Edge Function's response shape; the function returns
 * 200 even when individual sends fail (#7b Option B partial success),
 * so the UI walks each recipient outcome.
 */
interface EmailVendorOutcome {
  vendor_id: string;
  vendor_name: string;
  recipient_user_id: string | null;
  recipient_email: string | null;
  item_count: number;
  message_id: string | null;
  /** True when no RESEND_API_KEY was set on the function side. */
  mock: boolean;
  /** Null on success; error code on failure. */
  error: string | null;
}

interface EmailSendResult {
  /** Per-vendor outcomes; empty array means no pending rows existed. */
  outcomes: EmailVendorOutcome[];
  /** When the whole call failed (auth, server, etc.). */
  topLevelError: string | null;
}

// ---- Helpers --------------------------------------------------------

function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

function displayName(
  user: Pick<UserRow, 'full_name' | 'email_cached'> | undefined,
): string {
  if (!user) return 'Someone';
  const name = user.full_name?.trim();
  if (name) return name;
  const email = user.email_cached?.trim();
  if (email) return email;
  return 'Someone';
}

/**
 * Approval timestamp → human-readable. Relative for recent
 * (< 7d), absolute date for older. "just now" for < 1 minute.
 */
function formatApprovalTime(iso: string): string {
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const diffMin = diffMs / 60_000;
  const diffHour = diffMin / 60;
  const diffDay = diffHour / 24;

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`;
  if (diffHour < 24) return `${Math.floor(diffHour)}h ago`;
  if (diffDay < 7) return `${Math.floor(diffDay)}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildGroups(items: QueueItem[]): VendorGroup[] {
  const map = new Map<string, VendorGroup>();
  for (const item of items) {
    const existing = map.get(item.vendor.id);
    if (existing) {
      existing.items.push(item);
      existing.subtotal += item.product.unit_price * item.qty;
    } else {
      map.set(item.vendor.id, {
        vendor: item.vendor,
        items: [item],
        subtotal: item.product.unit_price * item.qty,
      });
    }
  }
  // Stable order: alphabetical by vendor name.
  return Array.from(map.values()).sort((a, b) =>
    a.vendor.name.localeCompare(b.vendor.name),
  );
}

/**
 * Demo formula (2C.6, "Same helper as the demo, now against real
 * data"): order ~5 weeks of supply at the current burn rate, with
 * a floor of 1. Doesn't subtract current count — this is the *size*
 * of the reorder, not the shortfall. Returns null when burn rate
 * is null (no usage data → no suggestion to make).
 */
function suggestedQtyFromBurnRate(monthlyBurn: number | null): number | null {
  if (monthlyBurn === null) return null;
  const suggested = Math.ceil(monthlyBurn * 1.2);
  return Math.max(suggested, 1);
}

// ---- Top-level component --------------------------------------------

export default function ReorderPage() {
  const session = useSession();
  const navigate = useNavigate();
  const { activeMembership, userId } = session;
  const clinicId = activeMembership.clinic_id;
  const canEdit = hasPermission(session, 'reorder.draft.edit');

  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [retryToken, setRetryToken] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [vendorActionPending, setVendorActionPending] = useState<Set<string>>(
    new Set(),
  );
  const [approvingAll, setApprovingAll] = useState(false);
  const [filter, setFilter] = useState<FilterTab>('pending');
  // Cache of clinic-mate users keyed by id, kept across approves so
  // optimistic updates can name the approver without a refetch.
  const [usersById, setUsersById] = useState<Map<string, UserRow>>(new Map());

  // 2D.1 — "Send approval emails" CTA state.
  // sendingEmails is the in-flight pending flag for the button.
  // emailSendResult holds the most recent send outcome for the
  // success/error banner. Null when nothing has been sent this
  // session, or when the banner has been dismissed.
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailSendResult, setEmailSendResult] =
    useState<EmailSendResult | null>(null);

  // ---- Fetch ---------------------------------------------------------
  // Three parallel queries plus a follow-up users fetch — the queue
  // (both pending and sent), products, vendors, then users for any
  // approvers + the current user. Joining client-side mirrors
  // InventoryPage and avoids PostgREST's composite-FK resolution.
  // Sizes are tiny (~tens of rows in each).

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    async function load(): Promise<void> {
      const [queueResult, productsResult, vendorsResult, burnResult] =
        await Promise.all([
          supabase
            .from('reorder_queue')
            .select(
              'id, qty, status, product_id, vendor_id, approved_by, approved_at, order_id',
            )
            .eq('clinic_id', clinicId)
            .in('status', ['pending', 'sent'])
            .order('submitted_at', { ascending: false }),
          supabase
            .from('products')
            .select('id, name, uom, unit_price')
            .eq('clinic_id', clinicId),
          supabase
            .from('vendors')
            .select('id, name')
            .eq('clinic_id', clinicId),
          // 4A.1: read from the rolling-burn-rate mat view rather
          // than aggregating client-side. Same data shape consumed
          // downstream — suggestedQtyFromBurnRate(monthlyBurn) is
          // unchanged. View refreshes nightly; intraday writes
          // (scans, receipts) don't move the suggestion until the
          // next tick, which matches the "trend" semantic this
          // hint represents.
          supabase
            .from('product_burn_rates')
            .select('product_id, burn_rate_4w')
            .eq('clinic_id', clinicId),
        ]);

      if (cancelled) return;

      if (queueResult.error) {
        console.error('[viashift] Reorder queue fetch failed:', queueResult.error);
        setState({ kind: 'error', message: queueResult.error.message });
        return;
      }
      if (productsResult.error) {
        console.error(
          '[viashift] Reorder products fetch failed:',
          productsResult.error,
        );
        setState({ kind: 'error', message: productsResult.error.message });
        return;
      }
      if (vendorsResult.error) {
        console.error(
          '[viashift] Reorder vendors fetch failed:',
          vendorsResult.error,
        );
        setState({ kind: 'error', message: vendorsResult.error.message });
        return;
      }
      if (burnResult.error) {
        console.error(
          '[viashift] Reorder burn rates fetch failed:',
          burnResult.error,
        );
        setState({ kind: 'error', message: burnResult.error.message });
        return;
      }

      // Resolve user display names for any approvers + current user.
      // public.users RLS allows reading self + clinic-mates, so this
      // succeeds for any approver who is also a clinic member (every
      // approver should be — they had to be a manager+ at approve time).
      const userIdsNeeded = new Set<string>();
      userIdsNeeded.add(userId);
      for (const row of queueResult.data ?? []) {
        if (row.approved_by) userIdsNeeded.add(row.approved_by);
      }

      const userMap = new Map<string, UserRow>();
      const usersResult = await supabase
        .from('users')
        .select('*')
        .in('id', Array.from(userIdsNeeded));

      if (cancelled) return;

      if (usersResult.error) {
        console.error(
          '[viashift] Reorder users fetch failed:',
          usersResult.error,
        );
        setState({ kind: 'error', message: usersResult.error.message });
        return;
      }

      for (const u of usersResult.data ?? []) {
        userMap.set(u.id, u);
      }

      // Index lookups so the join is O(n).
      const productById = new Map<
        string,
        Pick<ProductRow, 'id' | 'name' | 'uom' | 'unit_price'>
      >();
      for (const row of productsResult.data ?? []) {
        productById.set(row.id, row);
      }
      const vendorById = new Map<string, Pick<VendorRow, 'id' | 'name'>>();
      for (const row of vendorsResult.data ?? []) {
        vendorById.set(row.id, row);
      }

      // 4A.1: build the burn-rate map directly from the
      // product_burn_rates view. Products with null rates (no
      // qualifying consumption in window) are absent from the
      // map; .get() returns undefined → defaults to null
      // downstream, matching the prior "no usage" path.
      const burnByProduct = new Map<string, number>();
      for (const row of burnResult.data ?? []) {
        // 2026-05-19: product_id narrowed for the regen — the
        // wrapper view can't propagate NOT NULL from products.id
        // through PostgREST, so the type is `string | null` even
        // though it's never null in practice. Same fix landed on
        // InventoryPage in the same delivery.
        if (row.product_id !== null && row.burn_rate_4w !== null) {
          burnByProduct.set(row.product_id, row.burn_rate_4w);
        }
      }

      const items: QueueItem[] = [];
      for (const row of queueResult.data ?? []) {
        if (!isQueueStatus(row.status)) {
          console.error(
            '[viashift] Unexpected reorder_queue.status, skipping:',
            row,
          );
          continue;
        }
        const product = productById.get(row.product_id);
        const vendor = vendorById.get(row.vendor_id);
        if (!product || !vendor) {
          console.error(
            '[viashift] Reorder queue row missing product/vendor:',
            row,
          );
          continue;
        }

        // Only sent rows carry approval info.
        let approval: ApprovalInfo | null = null;
        if (row.status === 'sent' && row.approved_at && row.approved_by) {
          approval = {
            byName: displayName(userMap.get(row.approved_by)),
            at: row.approved_at,
          };
        }

        // Burn-rate-derived suggestion. monthlyBurn is null
        // when the product has no qualifying consumption in
        // the window — suggestedQtyFromBurnRate hides the hint.
        const monthlyBurn = burnByProduct.get(row.product_id) ?? null;
        const suggestedQty = suggestedQtyFromBurnRate(monthlyBurn);

        items.push({
          id: row.id,
          qty: row.qty,
          status: row.status,
          product,
          vendor,
          approval,
          orderId: (row as { order_id?: string | null }).order_id ?? null,
          suggestedQty,
        });
      }

      setUsersById(userMap);
      setState({ kind: 'loaded', items });
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [clinicId, userId, retryToken]);

  // ---- Mutations -----------------------------------------------------

  const onRetry = useCallback(() => setRetryToken((t) => t + 1), []);

  const updateQty = useCallback(
    async (rowId: string, nextQty: number): Promise<void> => {
      // Schema CHECK forbids qty <= 0; bail before the round-trip.
      if (nextQty < 1) return;

      // Optimistic local update via the updater function (only safe
      // way to read latest state inside a useCallback whose closure
      // may be stale). On failure, refetch — captured-via-updater
      // values are racy across React's batched renders, and a refetch
      // is one round-trip we already pay on retry anyway.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          items: prev.items.map((it) =>
            it.id === rowId ? { ...it, qty: nextQty } : it,
          ),
        };
      });

      const { error } = await supabase
        .from('reorder_queue')
        .update({ qty: nextQty })
        .eq('id', rowId);

      if (error) {
        console.error('[viashift] Reorder qty update failed:', error);
        setRetryToken((t) => t + 1);
        setActionError("Couldn't update quantity — try again");
      }
    },
    [],
  );

  // Single approval path. Vendor-scoped and approve-all both funnel
  // through here. When 2D lands, this is the single call site to swap
  // for the email-trigger path.
  // 2D.5 — Approve via the same Edge Function as the email-link
  // path. The PWA can't mint approval JWTs (secret is server-side),
  // so the function dispatches on body shape: { vendor_id, qids }
  // triggers the session-authed in-app path. End state matches the
  // email path — `approve_reorder_batch` creates orders/order_items
  // and flips queue rows to 'sent', then `triggerVendorOrderEmail`
  // fires server-side. UI is non-optimistic: local stamp happens
  // after the function returns ok.
  const approveVendor = useCallback(
    async (
      vendorId: string,
      ids: string[],
    ): Promise<{ ok: boolean; reason?: string }> => {
      if (!canEdit || ids.length === 0) return { ok: true };

      setVendorActionPending((prev) => {
        const next = new Set(prev);
        next.add(vendorId);
        return next;
      });
      setActionError(null);

      let outcome: { ok: boolean; reason?: string } = { ok: false };

      try {
        const { data, error } = await supabase.functions.invoke<{
          ok: boolean;
          status:
            | 'approved'
            | 'already_approved'
            | 'invalid'
            | 'expired'
            | 'error';
          order_id?: string;
          reason?: string;
        }>('handle-approval-link', {
          body: { vendor_id: vendorId, qids: ids },
        });

        if (error) {
          console.error('[viashift] approveVendor invoke error', error);
          outcome = { ok: false, reason: 'invoke_failed' };
        } else if (!data) {
          outcome = { ok: false, reason: 'no_response' };
        } else if (data.ok) {
          // Stamp affected items 'sent' with the caller's identity.
          // On `already_approved` the actual approver may be someone
          // else (email-link race or a parallel session); the next
          // refetch corrects the displayed approver name.
          const idSet = new Set(ids);
          const myName = displayName(usersById.get(userId));
          const approvedAtIso = new Date().toISOString();

          setState((prev) => {
            if (prev.kind !== 'loaded') return prev;
            return {
              kind: 'loaded',
              items: prev.items.map((it) =>
                idSet.has(it.id)
                  ? {
                      ...it,
                      status: 'sent' as const,
                      approval: { byName: myName, at: approvedAtIso },
                    }
                  : it,
              ),
            };
          });

          outcome = { ok: true };
        } else {
          outcome = { ok: false, reason: data.reason ?? data.status };
        }
      } catch (err) {
        console.error('[viashift] approveVendor threw', err);
        outcome = { ok: false, reason: 'unexpected' };
      } finally {
        setVendorActionPending((prev) => {
          const next = new Set(prev);
          next.delete(vendorId);
          return next;
        });
      }

      if (!outcome.ok) {
        setActionError("Couldn't approve — try again");
      }

      return outcome;
    },
    [canEdit, userId, usersById],
  );

  // 2D.5 — Approve all pending vendor groups by fanning out one
  // Edge Function call per vendor in parallel. Each approveVendor()
  // manages its own per-vendor pending state and stamps its items
  // on success, so partial success degrades gracefully — vendors
  // that succeed flip to 'sent' immediately, vendors that fail stay
  // pending and surface in the error banner with their names. The
  // partial-success message overrides the per-vendor "Couldn't
  // approve — try again" banner that approveVendor would otherwise
  // set on each failed call.
  const approveAll = useCallback(
    async (vendorGroups: VendorGroup[]): Promise<void> => {
      if (!canEdit || vendorGroups.length === 0) return;

      setApprovingAll(true);
      setActionError(null);

      const results = await Promise.all(
        vendorGroups.map((g) =>
          approveVendor(
            g.vendor.id,
            g.items.map((it) => it.id),
          ).then((res) => ({ vendor: g.vendor.name, ...res })),
        ),
      );

      setApprovingAll(false);

      const failures = results.filter((r) => !r.ok);
      if (failures.length === 0) {
        // Clear any per-vendor errors that approveVendor may have set.
        setActionError(null);
      } else if (failures.length === results.length) {
        setActionError("Couldn't approve — try again");
      } else {
        const names = failures.map((f) => f.vendor).join(', ');
        setActionError(
          `Approved ${results.length - failures.length} of ${results.length} — failed: ${names}`,
        );
      }
    },
    [canEdit, approveVendor],
  );

  // 2D.1 — Send approval emails for all pending rows in this clinic.
  // The Edge Function infers the clinic from the caller's auth JWT
  // and groups by vendor server-side; we just invoke it. The response
  // is a per-vendor outcome array — surfaced via emailSendResult.
  // No optimistic UI change: queue rows do NOT transition to 'sent'
  // here (that happens in 2D.2 when the manager clicks the link).
  const sendApprovalEmails = useCallback(async (): Promise<void> => {
    if (!canEdit || sendingEmails) return;

    setSendingEmails(true);
    setEmailSendResult(null);
    setActionError(null);

    try {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        sent?: EmailVendorOutcome[];
        reason?: string;
        error?: string;
      }>('generate-approval-email', { body: {} });

      if (error) {
        console.error('[viashift] sendApprovalEmails invoke error', error);
        setEmailSendResult({
          outcomes: [],
          topLevelError: 'invoke_failed',
        });
        return;
      }

      if (!data || !data.ok) {
        setEmailSendResult({
          outcomes: [],
          topLevelError: data?.error ?? 'unknown_error',
        });
        return;
      }

      setEmailSendResult({
        outcomes: data.sent ?? [],
        topLevelError: null,
      });
    } catch (err) {
      console.error('[viashift] sendApprovalEmails threw', err);
      setEmailSendResult({
        outcomes: [],
        topLevelError: 'unexpected',
      });
    } finally {
      setSendingEmails(false);
    }
  }, [canEdit, sendingEmails]);

  // Cancel a single pending row. Soft-delete via status='cancelled'
  // — the row stays in the table for audit, and the partial unique
  // index on pending status releases its slot so the same product
  // can be re-enqueued (manually or by the 2C.2 trigger) without
  // colliding. Optimistic: remove from local state immediately,
  // refetch on error to restore the row if the UPDATE failed.
  const cancelItem = useCallback(
    async (rowId: string): Promise<void> => {
      if (!canEdit) return;

      setActionError(null);

      // Optimistic remove. Capture nothing for rollback — refetch on
      // error is simpler and the user-visible result is the same.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          items: prev.items.filter((it) => it.id !== rowId),
        };
      });

      const { error } = await supabase
        .from('reorder_queue')
        .update({ status: 'cancelled' })
        .eq('id', rowId);

      if (error) {
        console.error('[viashift] Reorder cancel failed:', error);
        setRetryToken((t) => t + 1);
        setActionError("Couldn't remove item — try again");
      }
    },
    [canEdit],
  );

  // ---- Render --------------------------------------------------------

  // Compute filtered items + groups + counts unconditionally so the
  // early-return branches don't have to redo their own bookkeeping.
  const allItems = useMemo(
    () => (state.kind === 'loaded' ? state.items : []),
    [state],
  );
  const pendingCount = useMemo(
    () => allItems.filter((it) => it.status === 'pending').length,
    [allItems],
  );
  const sentCount = useMemo(
    () => allItems.filter((it) => it.status === 'sent').length,
    [allItems],
  );
  const filteredItems = useMemo(
    () => allItems.filter((it) => it.status === filter),
    [allItems, filter],
  );
  const groups = useMemo(() => buildGroups(filteredItems), [filteredItems]);
  const totalSubtotal = useMemo(
    () => groups.reduce((s, g) => s + g.subtotal, 0),
    [groups],
  );

  if (state.kind === 'loading') {
    return (
      <Shell>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Shell>
    );
  }

  if (state.kind === 'error') {
    return (
      <Shell>
        <ErrorCard message={state.message} onRetry={onRetry} />
      </Shell>
    );
  }

  return (
    <Shell>
      <PermissionRequiredBanner
        permission="reorder.draft.edit"
        message="Approving and editing reorder quantities is available to managers and owners. You can still review what's queued."
      />

      {actionError && (
        <ActionErrorBanner
          message={actionError}
          onDismiss={() => setActionError(null)}
        />
      )}

      {/* Approve-all banner — pending tab only, manager+ only. */}
      {canEdit && filter === 'pending' && groups.length > 0 && (
        <ApproveAllBanner
          itemCount={filteredItems.length}
          vendorCount={groups.length}
          onApproveAll={() => {
            // I-1 — native confirm before any dispatch. Text is
            // computed from the current pending set at press time;
            // cancel returns with zero side effects, confirm
            // preserves the existing fan-out end-to-end. The dialog
            // shows even for a single vendor — the friction is the
            // feature (Drew 2026-05-26 #8 / proposal F).
            const orderWord = filteredItems.length === 1 ? 'order' : 'orders';
            const vendorWord = groups.length === 1 ? 'vendor' : 'vendors';
            const ok = window.confirm(
              `Approve ${filteredItems.length} ${orderWord} to ${groups.length} ${vendorWord} for ${formatCurrency(totalSubtotal)}?`,
            );
            if (!ok) return;
            void approveAll(groups);
          }}
          isPending={approvingAll}
        />
      )}

      {/* 2D.1 — Send approval emails (manager+, pending tab only).
          Distinct from Approve-all: this dispatches an email per
          vendor with a JWT approve link, while Approve-all is the
          in-app direct flip. 2D.5 unifies the two paths. */}
      {canEdit && filter === 'pending' && groups.length > 0 && (
        <EmailApprovalCard
          vendorCount={groups.length}
          onSend={() => {
            void sendApprovalEmails();
          }}
          isPending={sendingEmails}
          result={emailSendResult}
          onDismissResult={() => setEmailSendResult(null)}
        />
      )}

      <FilterTabs
        active={filter}
        pendingCount={pendingCount}
        sentCount={sentCount}
        onChange={setFilter}
      />

      {groups.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="px-4 pb-6">
          {groups.map((group) => (
            <VendorGroupCard
              key={group.vendor.id}
              group={group}
              filter={filter}
              canEdit={canEdit}
              onUpdateQty={updateQty}
              onCancel={cancelItem}
              onApprove={() => {
                void approveVendor(
                  group.vendor.id,
                  group.items.map((it) => it.id),
                );
              }}
              isApprovePending={vendorActionPending.has(group.vendor.id)}
              onReceive={(orderId) => navigate(`/receive/${orderId}`)}
            />
          ))}
        </div>
      )}
    </Shell>
  );
}

// ---- Shell ----------------------------------------------------------

function Shell({ children }: { children: ReactNode }) {
  return <div className="pb-6">{children}</div>;
}

// ---- Filter tabs ---------------------------------------------------

interface FilterTabsProps {
  active: FilterTab;
  pendingCount: number;
  sentCount: number;
  onChange: (next: FilterTab) => void;
}

function FilterTabs({
  active,
  pendingCount,
  sentCount,
  onChange,
}: FilterTabsProps) {
  const tabs: { id: FilterTab; label: string; count: number }[] = [
    { id: 'pending', label: 'Pending', count: pendingCount },
    { id: 'sent', label: 'Sent', count: sentCount },
  ];
  return (
    <div
      role="tablist"
      aria-label="Reorder queue filter"
      className="flex items-center gap-1 mx-4 mt-3 mb-3 p-1 bg-slate-100 rounded-lg"
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[13px] font-medium transition ${
              isActive
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label}
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full tabular-nums ${
                isActive
                  ? 'bg-slate-100 text-slate-700'
                  : 'bg-slate-200 text-slate-600'
              }`}
            >
              {tab.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---- Approve-all banner --------------------------------------------

interface ApproveAllBannerProps {
  itemCount: number;
  vendorCount: number;
  onApproveAll: () => void;
  isPending: boolean;
}

function ApproveAllBanner({
  itemCount,
  vendorCount,
  onApproveAll,
  isPending,
}: ApproveAllBannerProps) {
  return (
    <div className="mx-4 mt-4 mb-2 p-3 rounded-2xl bg-gradient-to-br from-action to-action-hover text-white">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-action-weak">
            Approval needed
          </div>
          <div className="text-[15px] font-semibold mt-0.5">
            {itemCount} item{itemCount === 1 ? '' : 's'} across {vendorCount}{' '}
            vendor{vendorCount === 1 ? '' : 's'}
          </div>
          {/* Update #9 (2026-07-17): "Est. total $X" removed — no dollar
              figures on the Reorder screen. The approve-all confirm
              dialog still shows the total before commit. */}
        </div>
        <button
          type="button"
          onClick={onApproveAll}
          disabled={isPending}
          className="shrink-0 h-10 px-3.5 rounded-lg bg-white text-action text-[13px] font-semibold hover:bg-action-weak active:scale-95 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isPending ? 'Approving…' : 'Approve all'}
        </button>
      </div>
    </div>
  );
}

// ---- Email approval card (2D.1) ------------------------------------

interface EmailApprovalCardProps {
  vendorCount: number;
  onSend: () => void;
  isPending: boolean;
  result: EmailSendResult | null;
  onDismissResult: () => void;
}

function EmailApprovalCard({
  vendorCount,
  onSend,
  isPending,
  result,
  onDismissResult,
}: EmailApprovalCardProps) {
  return (
    <div className="mx-4 mt-2 mb-2 p-3 rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Approve by email
          </div>
          <div className="text-[14px] font-semibold mt-0.5 text-slate-900">
            Send approval link to managers
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            One email per vendor &middot; {vendorCount} vendor
            {vendorCount === 1 ? '' : 's'} pending
          </div>
        </div>
        <button
          type="button"
          onClick={onSend}
          disabled={isPending}
          className="shrink-0 h-10 px-3.5 rounded-lg bg-slate-900 text-white text-[13px] font-semibold hover:bg-slate-800 active:scale-95 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isPending ? 'Sending…' : 'Send emails'}
        </button>
      </div>

      {result !== null && (
        <EmailSendResultLine result={result} onDismiss={onDismissResult} />
      )}
    </div>
  );
}

interface EmailSendResultLineProps {
  result: EmailSendResult;
  onDismiss: () => void;
}

/**
 * Renders the per-vendor outcomes (or a top-level error) below the
 * Send button. Mock-mode (no RESEND_API_KEY) is called out
 * explicitly so it's obvious during the pre-Resend-verification
 * window that nothing actually went out.
 */
function EmailSendResultLine({ result, onDismiss }: EmailSendResultLineProps) {
  const { outcomes, topLevelError } = result;

  if (topLevelError) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-start justify-between gap-3">
        <div className="text-[12px] text-red-600">
          Couldn&apos;t send approval emails ({topLevelError}).
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] text-slate-500 hover:text-slate-700"
        >
          Dismiss
        </button>
      </div>
    );
  }

  if (outcomes.length === 0) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-start justify-between gap-3">
        <div className="text-[12px] text-slate-600">
          Nothing to send &mdash; no pending items.
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] text-slate-500 hover:text-slate-700"
        >
          Dismiss
        </button>
      </div>
    );
  }

  const sentCount = outcomes.filter((o) => o.error === null).length;
  const errorCount = outcomes.length - sentCount;
  const anyMock = outcomes.some((o) => o.mock);

  // #7b: outcomes are now per-(vendor × recipient). Summarize as
  // distinct recipients reached and distinct vendors, computed from
  // the successful rows.
  const sentRows = outcomes.filter((o) => o.error === null);
  const distinctRecipients = new Set(
    sentRows.map((o) => o.recipient_user_id).filter((id) => id !== null),
  ).size;
  const distinctVendors = new Set(sentRows.map((o) => o.vendor_id)).size;

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[12px] text-slate-700">
          {sentCount > 0 && (
            <span>
              Sent to {distinctRecipients} recipient
              {distinctRecipients === 1 ? '' : 's'} for{' '}
              {distinctVendors} vendor{distinctVendors === 1 ? '' : 's'}
            </span>
          )}
          {errorCount > 0 && sentCount > 0 && <span> &middot; </span>}
          {errorCount > 0 && (
            <span className="text-red-600">
              {errorCount} failed
            </span>
          )}
          {anyMock && (
            <span className="ml-1 text-amber-600">
              (mock &mdash; RESEND_API_KEY not set)
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] text-slate-500 hover:text-slate-700"
        >
          Dismiss
        </button>
      </div>
      {errorCount > 0 && (
        <ul className="mt-2 space-y-1">
          {outcomes
            .filter((o) => o.error !== null)
            .map((o) => (
              <li key={o.vendor_id} className="text-[11px] text-red-600">
                {o.vendor_name}: {o.error}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

// ---- Vendor group card ---------------------------------------------

interface VendorGroupCardProps {
  group: VendorGroup;
  filter: FilterTab;
  canEdit: boolean;
  onUpdateQty: (rowId: string, nextQty: number) => Promise<void>;
  onCancel: (rowId: string) => Promise<void>;
  onApprove: () => void;
  isApprovePending: boolean;
  onReceive: (orderId: string) => void;
}

function VendorGroupCard({
  group,
  filter,
  canEdit,
  onUpdateQty,
  onCancel,
  onApprove,
  isApprovePending,
  onReceive,
}: VendorGroupCardProps) {
  const [open, setOpen] = useState(true);
  const itemCount = group.items.length;
  // Sent items render read-only regardless of role; only pending +
  // canEdit puts the qty stepper and approve footer in scope.
  const showEditAffordances = filter === 'pending' && canEdit;
  // All sent rows in a vendor group share one order (approve creates
  // one order per vendor), so the group's order_id is the receive
  // target. Take the first row that carries one.
  const groupOrderId =
    group.items.find((it) => it.orderId)?.orderId ?? null;

  return (
    <div className="mb-3 bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition"
        aria-expanded={open}
      >
        <div className="text-left min-w-0">
          <div className="text-[14px] font-semibold text-slate-900 truncate">
            {group.vendor.name}
          </div>
          {/* Update #9 §2/§3 (2026-07-17): no dollar figures anywhere
              on the Reorder screen (decided 2026-06-01). Subtotal is
              still computed — it feeds the approve-all native confirm
              dialog, which deliberately keeps the total as the
              spend-visibility backstop. */}
          <div className="text-[11px] text-slate-500 mt-0.5">
            {itemCount} item{itemCount === 1 ? '' : 's'}
          </div>
        </div>
        <Icon
          name={open ? 'chevdown' : 'chevright'}
          className="w-4 h-4 text-slate-400 shrink-0"
          strokeWidth={2.5}
        />
      </button>

      {open && (
        <div className="border-t border-slate-100">
          {group.items.map((item) => (
            <QueueItemRow
              key={item.id}
              item={item}
              filter={filter}
              canEdit={canEdit}
              onUpdateQty={onUpdateQty}
              onCancel={onCancel}
            />
          ))}

          {showEditAffordances && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
              <button
                type="button"
                onClick={onApprove}
                disabled={isApprovePending}
                className="w-full h-10 rounded-lg bg-action text-white text-[13px] font-semibold hover:bg-action-hover active:bg-action-active transition flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Icon name="check" className="w-3.5 h-3.5" strokeWidth={2.5} />
                {isApprovePending ? 'Approving…' : 'Approve & send'}
              </button>
            </div>
          )}

          {/* Sent tab — receive the order. One order per vendor group,
              so the receive entry lives once at the group footer. */}
          {filter === 'sent' && groupOrderId && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
              <button
                type="button"
                onClick={() => onReceive(groupOrderId)}
                className="w-full h-10 rounded-lg bg-[#2A7F8E] text-white text-[13px] font-semibold hover:bg-[#246e7b] active:bg-[#1f606b] transition flex items-center justify-center gap-1.5"
              >
                <Icon name="check" className="w-3.5 h-3.5" strokeWidth={2.5} />
                Receive order
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Queue item row ------------------------------------------------

interface QueueItemRowProps {
  item: QueueItem;
  filter: FilterTab;
  canEdit: boolean;
  onUpdateQty: (rowId: string, nextQty: number) => Promise<void>;
  onCancel: (rowId: string) => Promise<void>;
}

function QueueItemRow({
  item,
  filter,
  canEdit,
  onUpdateQty,
  onCancel,
}: QueueItemRowProps) {
  const showStepper = filter === 'pending' && canEdit;
  const showRemove = filter === 'pending' && canEdit;
  const showSuggestion = showStepper && item.suggestedQty !== null;
  const matchesSuggestion =
    item.suggestedQty !== null && item.suggestedQty === item.qty;

  return (
    <div className="px-4 py-3 border-b border-slate-100 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium text-slate-900 truncate">
            {item.product.name}
          </div>
          {/* Update #9 (2026-07-17): per-unit price + line total removed —
              no dollar figures on this screen (decided 2026-06-01; the
              live per-unit render was a bug, not a design change). */}
          <div className="text-[11px] text-slate-500 truncate">
            {item.product.uom}
          </div>
        </div>
        {showStepper ? (
          <div className="flex flex-col items-end gap-1 shrink-0">
            <QtyStepper
              qty={item.qty}
              onAdjust={(delta) => onUpdateQty(item.id, item.qty + delta)}
            />
            {showSuggestion && (
              <BurnRateHint
                suggested={item.suggestedQty as number}
                matches={matchesSuggestion}
                onApply={() => {
                  if (item.suggestedQty !== null) {
                    void onUpdateQty(item.id, item.suggestedQty);
                  }
                }}
              />
            )}
          </div>
        ) : (
          <div className="text-[13px] font-semibold text-slate-900 shrink-0 tabular-nums">
            ×{item.qty}
          </div>
        )}
      </div>

      {/* Remove from queue — manager+, pending only. Soft-delete via
          status='cancelled'; preserves the row for audit. */}
      {showRemove && (
        <button
          type="button"
          onClick={() => {
            void onCancel(item.id);
          }}
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-red-600 transition"
        >
          <Icon name="x" className="w-3 h-3" strokeWidth={2.5} />
          <span>Remove from queue</span>
        </button>
      )}

      {/* Approval trail — sent rows only, regardless of role. */}
      {filter === 'sent' && item.approval && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-700">
          <Icon name="check" className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span>
            Approved by {item.approval.byName} ·{' '}
            {formatApprovalTime(item.approval.at)}
          </span>
        </div>
      )}
    </div>
  );
}

// ---- Burn-rate hint (manager+, pending only) -----------------------

interface BurnRateHintProps {
  /** Suggested qty from the 4-week burn rate. Caller has already
   *  verified it's non-null before rendering this component. */
  suggested: number;
  /** True when the item's current qty matches the suggestion. */
  matches: boolean;
  /** Tap handler — applies the suggestion via updateQty. Ignored
   *  in the matches case (the hint renders as a non-button label). */
  onApply: () => void;
}

function BurnRateHint({ suggested, matches, onApply }: BurnRateHintProps) {
  // Matches: informational tag — qty already aligns with the smart
  // suggestion. Non-matches: actionable pill — one tap sets qty.
  if (matches) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-green-700 font-medium">
        <Icon name="trending" className="w-2.5 h-2.5" strokeWidth={2} />
        <span>4-week burn rate</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onApply}
      className="inline-flex items-center gap-1 text-[10px] text-green-600 font-medium hover:text-green-700 active:scale-95 transition"
      title={`Set quantity to ${suggested} (4-week burn rate)`}
    >
      <Icon name="trending" className="w-2.5 h-2.5" strokeWidth={2} />
      <span>suggest {suggested}</span>
    </button>
  );
}

// ---- Qty stepper (manager+ only, pending only) ---------------------

interface QtyStepperProps {
  qty: number;
  onAdjust: (delta: number) => Promise<void>;
}

function QtyStepper({ qty, onAdjust }: QtyStepperProps) {
  // Disable `−` at qty=1: schema CHECK forbids qty <= 0 and there's
  // no semantic for "queue with zero of this item" — for that the
  // user should remove the item (ticket 2C.5).
  const decrementDisabled = qty <= 1;

  return (
    <div className="flex items-center gap-1 bg-surface rounded-lg p-0.5 shrink-0">
      <button
        type="button"
        onClick={() => {
          void onAdjust(-1);
        }}
        disabled={decrementDisabled}
        aria-label="Decrease quantity"
        className="w-7 h-7 rounded-md bg-white border border-slate-200 text-slate-700 flex items-center justify-center active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Icon name="minus" className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      <div
        className="w-7 text-center text-[14px] font-mono font-medium text-slate-900 tabular-nums"
        aria-live="polite"
        aria-atomic="true"
      >
        {qty}
      </div>
      <button
        type="button"
        onClick={() => {
          void onAdjust(1);
        }}
        aria-label="Increase quantity"
        className="w-7 h-7 rounded-md bg-white border border-slate-200 text-slate-700 flex items-center justify-center active:scale-95 transition"
      >
        <Icon name="plus" className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ---- Sub-states ----------------------------------------------------

function EmptyState({ filter }: { filter: FilterTab }) {
  const copy =
    filter === 'pending'
      ? {
          icon: 'check' as const,
          title: 'Nothing pending',
          body: "Everything's stocked or already on order. New items show up here when stock drops below par.",
          iconBg: 'bg-emerald-50',
          iconColor: 'text-emerald-600',
        }
      : {
          icon: 'cart' as const,
          title: 'No orders sent yet',
          body: 'Approved orders will appear here once you approve a vendor group.',
          iconBg: 'bg-slate-100',
          iconColor: 'text-slate-400',
        };

  return (
    <div className="px-4 pt-4">
      <div className="bg-white border border-slate-200 rounded-2xl py-10 px-6 flex flex-col items-center text-center">
        <div
          className={`w-12 h-12 rounded-2xl ${copy.iconBg} flex items-center justify-center mb-3`}
        >
          <Icon
            name={copy.icon}
            className={`w-6 h-6 ${copy.iconColor}`}
            strokeWidth={2.25}
          />
        </div>
        <div className="text-sm font-semibold text-slate-900">{copy.title}</div>
        <div className="text-xs text-slate-500 mt-1 leading-relaxed max-w-[260px]">
          {copy.body}
        </div>
      </div>
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="px-4 pt-8">
      <div className="bg-white border border-amber-200 rounded-2xl p-5 flex flex-col items-center text-center">
        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mb-3">
          <Icon
            name="shield"
            className="w-5 h-5 text-amber-600"
            strokeWidth={2}
          />
        </div>
        <div className="text-sm font-semibold text-slate-900">
          Couldn't load reorder queue
        </div>
        <div className="text-xs text-slate-500 mt-1 leading-relaxed max-w-[260px]">
          Check your connection and retry. Refreshing the page also fixes most
          network blips.
        </div>
        <div className="text-[10px] text-slate-400 mt-2 font-mono break-words max-w-full">
          {message}
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 h-11 px-5 rounded-xl bg-action hover:bg-action-hover active:bg-action-active transition text-sm font-semibold text-white"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

interface ActionErrorBannerProps {
  message: string;
  onDismiss: () => void;
}

function ActionErrorBanner({ message, onDismiss }: ActionErrorBannerProps) {
  return (
    <div
      role="alert"
      className="mx-4 mt-3 mb-2 p-3 rounded-xl bg-red-50 border border-red-100 flex items-start gap-2.5"
    >
      <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
        <Icon name="shield" className="w-4 h-4 text-red-700" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0 text-[12px] text-red-900 leading-snug">
        <div className="font-semibold">{message}</div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss error"
        className="w-7 h-7 rounded-md hover:bg-red-100 flex items-center justify-center shrink-0 transition"
      >
        <Icon name="x" className="w-3.5 h-3.5 text-red-700" strokeWidth={2.5} />
      </button>
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="w-7 h-7 border-[3px] border-slate-200 border-t-action rounded-full animate-spin"
      role="status"
      aria-label="Loading reorder queue"
    />
  );
}
